import React, { createContext, useContext, useEffect, useState } from 'react';
import type { SiteContent } from './types';
import defaultsJson from './defaults.json';

const defaults = defaultsJson as unknown as SiteContent;

interface Ctx { content: SiteContent; loaded: boolean; refresh: () => Promise<void> }
const ContentContext = createContext<Ctx>({ content: defaults, loaded: false, refresh: async () => {} });

export const ContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<SiteContent>(defaults);
  const [loaded, setLoaded] = useState(false);

  const refresh = async () => {
    try {
      const res = await fetch('/api/content', { headers: { accept: 'application/json' } });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      // Pro Bereich: Serverdaten gewinnen, fehlende Felder kommen aus den Defaults
      const merged = { ...defaults } as SiteContent;
      (Object.keys(defaults) as (keyof SiteContent)[]).forEach((key) => {
        if (data[key] && typeof data[key] === 'object') {
          (merged as any)[key] = { ...(defaults as any)[key], ...data[key] };
        }
      });
      setContent(merged);
    } catch {
      // Ohne API bleibt der eingebaute Stand sichtbar
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    document.title = content.settings.metaTitle || 'Dartware GmbH';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', content.settings.metaDescription || '');
  }, [content.settings.metaTitle, content.settings.metaDescription]);

  return <ContentContext.Provider value={{ content, loaded, refresh }}>{children}</ContentContext.Provider>;
};

export const useContent = () => useContext(ContentContext).content;
export const useContentState = () => useContext(ContentContext);
